import differenceInDays from 'date-fns/differenceInDays'
import format from 'date-fns/format'

import { CustomInvoiceAdminDto, InvoiceAdminDto, InvoiceClientDto } from '../types/api'
import { getTzDateInUtc } from './dates'

export const distributeAmountToAssociatedInvoices = (
  amount: number,
  mainInvoiceId: string,
  invoices: CustomInvoiceAdminDto[]
): Record<string, number> => {
  const result: Record<string, number> = {}
  if (!amount) {
    return result
  }

  let totalAmountDue = 0
  let invoiceIdToAllocateRemainingAmount: string | null = null
  const invoiceswithOutstandingBalance = invoices.filter((invoice) => {
    if (!invoice.id) {
      return false
    }
    const amountDue = invoice.amountDue || 0
    totalAmountDue += amountDue
    if (!amountDue) {
      return false
    }
    // Assign the mainInvoiceId or the first invoice with amountDue > 0
    if (!invoiceIdToAllocateRemainingAmount || mainInvoiceId === invoice.id) {
      invoiceIdToAllocateRemainingAmount = invoice.id
    }
    return true
  })
  if (!totalAmountDue || !invoiceswithOutstandingBalance.length) {
    result[mainInvoiceId] = amount
    return result
  }

  const rate = amount / totalAmountDue
  let totalAllocated = 0
  invoiceswithOutstandingBalance.forEach((invoice) => {
    if (!invoice.id || invoice.id === invoiceIdToAllocateRemainingAmount) {
      return
    }
    const amountDue = invoice.amountDue || 0
    const allocatedAmount = Math.round(amountDue * rate * 100) / 100
    if (allocatedAmount > 0) {
      result[invoice.id] = allocatedAmount
      totalAllocated += allocatedAmount
    }
  })

  // Explicitly round to 2 decimal places to prevent floating-point precision issues
  const remainingAmount = Math.round((amount - totalAllocated) * 100) / 100
  if (remainingAmount > 0 && invoiceIdToAllocateRemainingAmount) {
    result[invoiceIdToAllocateRemainingAmount] = remainingAmount
  }

  return result
}

export const getInvoiceDates = (invoice: InvoiceClientDto | InvoiceAdminDto) => {
  const invoicePeriods = invoice.period && invoice.period.length > 0 ? invoice.period : invoice.requestPeriods
  const { startDate, startTime, endDate, endTime } = invoicePeriods?.[0] || {}
  return {
    startDate: getTzDateInUtc(startDate, startTime, invoice.location?.timezone),
    endDate: getTzDateInUtc(endDate, endTime, invoice.location?.timezone),
    hasStartTime: !!startTime,
    hasEndTime: !!endTime,
  }
}

export const getServiceDateTextFormat = (
  minDate: Date | null,
  maxDate: Date | null,
  serviceName: string | undefined,
  invoicesCnt: number = 0,
  options = {}
) => {
  const mergedOptions = { fullYear: false, fullPeriod: false, fullYearLast: false, ...options }

  if (!minDate || !maxDate) {
    return 'N/A'
  }
  if (serviceName === 'daycare') {
    return invoicesCnt === 1 ? format(minDate, 'MMM d, yyyy') : 'Many'
  }

  if (differenceInDays(maxDate, minDate) <= 1 && !mergedOptions.fullPeriod) {
    return format(minDate, 'MMM d, yyyy')
  } else {
    return `${format(minDate, `MMM d${mergedOptions.fullYear ? ' yyyy' : ''}`)} - ${format(
      maxDate,
      `MMM d${mergedOptions.fullYear || mergedOptions.fullYearLast ? ' yyyy' : ''}`
    )}`
  }
}
